import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Handshake, ScrollText } from "lucide-react";

export default function GuildContract({ isLeader }) {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const refresh = async () => {
    try { setC(await api.guildContract()); } catch (e) { /* silent */ }
  };
  const refreshHistory = async () => {
    try { setHistory(await api.guildContractHistory()); } catch (e) { /* silent */ }
  };
  useEffect(() => {
    refresh();
    refreshHistory();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, []);

  if (!c) return null;
  const pct = Math.min(100, (c.progress / c.target) * 100);
  const ready = c.progress >= c.target && !c.claimed;

  const claim = async () => {
    setBusy(true);
    try {
      const r = await api.guildContractClaim();
      toast.success(`+${r.share}g to every member`);
      await refresh();
      await refreshHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="panel p-3 space-y-2" data-testid="guild-contract">
      <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] flex items-center gap-2">
        <Handshake className="w-4 h-4 text-[#B89C3A]" strokeWidth={1.5} /> Weekly Contract
        <span className="ml-auto font-mono text-[9px] text-[#666]">{c.week}</span>
      </div>
      <div className="font-body text-xs text-[#E2DFD8]">{c.text}</div>
      <div className="vbar" style={{ height: 8 }}>
        <span style={{ width: `${pct}%`, background: c.claimed ? "#4A5D23" : "#B89C3A" }} />
      </div>
      <div className="flex justify-between font-mono text-[10px] text-[#8c8c8c]">
        <span>{c.progress} / {c.target}</span>
        <span className="text-[#E5C34A]">Reward: {c.reward}g shared</span>
      </div>
      {c.claimed ? (
        <div className="text-center font-heading text-[10px] tracking-widest uppercase text-[#6E8B3D]">
          Claimed — rolls over next week
        </div>
      ) : ready ? (
        <button
          data-testid="contract-claim"
          onClick={claim}
          disabled={busy || !isLeader}
          className="w-full py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-xs tracking-widest uppercase disabled:opacity-30"
        >
          {isLeader ? "Claim (Leader)" : "Awaits Leader"}
        </button>
      ) : null}

      <div className="pt-1">
        <button
          data-testid="contract-history-toggle"
          onClick={() => setShowHistory(v => !v)}
          className="w-full flex items-center justify-center gap-2 py-1 border border-[#262626] text-[#8c8c8c] font-heading text-[10px] tracking-widest uppercase hover:text-[#E2DFD8]"
        >
          <ScrollText className="w-3 h-3" strokeWidth={1.5} />
          {showHistory ? "Hide History" : `Past Contracts (${history.length})`}
        </button>
        {showHistory && (
          <div className="mt-2 space-y-1" data-testid="contract-history">
            {history.length === 0 && (
              <p className="italic text-[#666] text-xs text-center py-2">
                No archived contracts yet. Time turns the page.
              </p>
            )}
            {history.map(h => {
              const complete = h.claimed;
              const pctH = Math.min(100, ((h.progress || 0) / (h.target || 1)) * 100);
              return (
                <div
                  key={h.week}
                  className="panel-inner p-2 space-y-1"
                  data-testid={`contract-history-${h.week}`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-mono text-[10px] text-[#8c8c8c] tracking-widest">{h.week}</span>
                    <span className={`font-heading text-[9px] tracking-widest uppercase ${complete ? "text-[#6E8B3D]" : "text-[#8A0303]"}`}>
                      {complete ? "Claimed" : "Failed"}
                    </span>
                  </div>
                  <div className="font-body text-[11px] text-[#B0ADA5] leading-tight">{h.text}</div>
                  <div className="vbar" style={{ height: 4 }}>
                    <span style={{ width: `${pctH}%`, background: complete ? "#4A5D23" : "#8A0303" }} />
                  </div>
                  <div className="flex justify-between font-mono text-[9px] text-[#666]">
                    <span>{h.progress || 0}/{h.target}</span>
                    <span>{h.reward}g</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
